import type {
  FetchGroupMembershipsArgs,
  FetchClientGroupByIdArgs,
  CreateClientGroupArgs,
  DeleteClientGroupArgs,
  FetchClientGroupsArgs,
  RemoveGroupMemberArgs,
  UpdateClientGroupArgs,
  AddGroupMemberArgs,
} from './types/clientGroups.types';
import type { ApiClientGroup } from '../schemas/clientGroups.schema';
import type { BaseApiResponse } from './types/api.types';

import { mapClientGroupFromApi } from './mappers/clientGroups.mappers';
import { destroy, patch, post, get } from './axios';

const baseUrl = (organizationId: string) =>
  `/v2/organizations/${organizationId}/client-groups`;

export async function fetchClientGroups({
  organizationId,
}: FetchClientGroupsArgs) {
  const response = await get<BaseApiResponse<ApiClientGroup[]>>(
    baseUrl(organizationId),
  );
  if (!response) return null;
  return {
    ...response,
    data: response.data.map(mapClientGroupFromApi),
  };
}

export async function fetchClientGroupById({
  id,
  organizationId,
}: FetchClientGroupByIdArgs) {
  const response = await get<BaseApiResponse<ApiClientGroup>>(
    `${baseUrl(organizationId)}/${id}`,
  );
  return response?.data ? mapClientGroupFromApi(response.data) : null;
}

export async function fetchGroupMemberships({
  organizationId,
}: FetchGroupMembershipsArgs) {
  const response = await get<BaseApiResponse<ApiClientGroup[]>>(
    `${baseUrl(organizationId)}/memberships`,
  );
  if (!response) return null;
  return {
    ...response,
    data: response.data.map(mapClientGroupFromApi),
  };
}

export async function createClientGroup({
  data,
  organizationId,
}: CreateClientGroupArgs) {
  const response = await post<ApiClientGroup>(baseUrl(organizationId), data);
  return response?.data ? mapClientGroupFromApi(response.data) : null;
}

export async function updateClientGroup({
  id,
  data,
  organizationId,
}: UpdateClientGroupArgs) {
  const response = await patch<ApiClientGroup>(
    `${baseUrl(organizationId)}/${id}`,
    data,
  );
  return response?.data ? mapClientGroupFromApi(response.data) : null;
}

export async function deleteClientGroup({
  id,
  organizationId,
}: DeleteClientGroupArgs) {
  await destroy(`${baseUrl(organizationId)}/${id}`);
}

export async function addGroupMember({
  clientOrgUserId,
  groupId,
  organizationId,
}: AddGroupMemberArgs) {
  const response = await post(`${baseUrl(organizationId)}/${groupId}/members`, {
    clientOrgUserId,
  });
  return response?.data ?? null;
}

export async function removeGroupMember({
  clientOrgUserId,
  groupId,
  organizationId,
}: RemoveGroupMemberArgs) {
  await destroy(
    `${baseUrl(organizationId)}/${groupId}/members/${clientOrgUserId}`,
  );
}
